import classNames from 'classnames';
import {VerificationInput} from 'modules/PersonalAccount/components/VerificationInput/VerificationInput';
import {useCountdown} from 'modules/PersonalAccount/hooks/useCountdown';
import {useVerificationPhone} from 'modules/PersonalAccount/hooks/useVerificationPhone';
import {
    ComponentProps,
    FC,
    useCallback,
    useEffect,
    useRef,
    useState,
} from 'react';

import {Modal} from 'components/Modal/Modal';
import {Text} from 'components/Text/Text';

import styles from './PhoneConfirmation.module.css';

//время в секундах через сколько можно повторно отправить смс
const TIMEOUT_SMS = 59;

type Props = {
    /** обработчик при ошибке запроса смс */
    onRequestPhoneVerifyError?: () => void;

    /** обработчик при успешном запросе смс */
    onRequestPhoneVerifySuccess?: () => void;

    /** обработчик при ошибке проверки смс */
    onVerifySmsError?: () => void;

    /** обработчик при успешной проверки смс */
    onVerifySmsSuccess?: (verificationToken: string) => void;

    /** номер телефона */
    phone: string;
} & ComponentProps<typeof Modal>;

export const PhoneConfirmation: FC<Props> = ({
    phone,
    onRequestPhoneVerifyError,
    onRequestPhoneVerifySuccess,
    onVerifySmsError,
    onVerifySmsSuccess,
    ...modalProps
}) => {
    const {start: startCountdown, secondsLeft} = useCountdown();
    const [codeValue, setCodeValue] = useState('');

    // После проверки смс-кода всегда выполняется другой запрос,
    // переданный из родителя, поэтому при выполнении этого внешнего запроса
    // нужно показывать состояние процессинга.
    // Для этого используется этот стейт
    const [isPending, setIsPending] = useState(false);

    const smsIsRequested = useRef(false);

    const {
        handleSendSms,
        verifySmsCode,
        verifySmsCodeIsError,
        verifySmsCodeMutationReset,
    } = useVerificationPhone({
        onVerifySmsCodeError: () => {
            setIsPending(false);
            onVerifySmsError?.();
        },
        onVerifySmsCodeSuccess: onVerifySmsSuccess,
        onRequestSmsCodeSuccess: () => {
            onRequestPhoneVerifySuccess?.();
        },
        onRequestSmsCodeError: () => {
            onRequestPhoneVerifyError?.();
        },
    });

    useEffect(() => {
        if (modalProps.visible && !smsIsRequested.current) {
            handleSendSms(phone);
            startCountdown(TIMEOUT_SMS);
            smsIsRequested.current = true;
        } else {
            setIsPending(false);
        }

        if (!modalProps.visible) {
            setCodeValue('');
            smsIsRequested.current = false;
        }
    }, [
        handleSendSms,
        modalProps.visible,
        phone,
        onRequestPhoneVerifyError,
        startCountdown,
    ]);

    const handleComplete = useCallback(
        (code: string) => {
            setIsPending(true);
            verifySmsCode(code);
        },
        [verifySmsCode],
    );

    const handleResend = useCallback(() => {
        startCountdown(TIMEOUT_SMS);
        handleSendSms(phone);
        setCodeValue('');
    }, [startCountdown, handleSendSms, phone]);

    const handleInputChange = useCallback(
        (value: string) => {
            setCodeValue(value);
            if (verifySmsCodeIsError) {
                verifySmsCodeMutationReset();
            }
        },
        [verifySmsCodeMutationReset, verifySmsCodeIsError],
    );

    return (
        <Modal {...modalProps} className={styles.modal}>
            <Text.Bold20 className={styles.title}>
                Мы&nbsp;отправили вам код в&nbsp;Telegram или&nbsp;SMS
            </Text.Bold20>
            <Text.Bold14 className={styles.subtitle}>
                Пожалуйста, проверьте оба канала и&nbsp;введите код ниже
            </Text.Bold14>

            <VerificationInput
                onComplete={handleComplete}
                className={classNames(styles.smsInput, {
                    [styles.error]: verifySmsCodeIsError,
                })}
                disabled={isPending}
                error={verifySmsCodeIsError}
                onChange={handleInputChange}
                value={codeValue}
            />

            <Text.Regular14 className={styles.phoneLabel}>
                Код выслан на номер:
            </Text.Regular14>
            <Text.Regular14 className={styles.phone}>
                {formatPhone(phone)}
            </Text.Regular14>

            {secondsLeft > 0 && (
                <>
                    <Text.Regular14 className={styles.sendLabel}>
                        Отправить код повторно через
                    </Text.Regular14>
                    <Text.Regular14 className={styles.time}>
                        {formatTime(secondsLeft)}
                    </Text.Regular14>
                </>
            )}
            {secondsLeft === 0 && (
                <Text.Regular14
                    className={styles.resendLabel}
                    onClick={handleResend}
                >
                    Отправить код повторно
                </Text.Regular14>
            )}
        </Modal>
    );
};

function formatPhone(phone: string) {
    return phone.split('').reduce((acc, char, index) => {
        if (index > 5 && index < phone.length - 2 && char !== ' ') {
            return acc + '*';
        }

        return acc + char;
    }, '');
}

function formatTime(seconds: number) {
    return `00:${seconds.toString().padStart(2, '0')}`;
}
